import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

export const fieldBase =
  'w-full rounded-sm border bg-surface text-ink placeholder:text-ink-subtle ' +
  'border-control-border shadow-[inset_0_1px_1px_rgb(4_18_34/0.04)] ' +
  'transition-[border-color,box-shadow,background-color] duration-150 ' +
  'hover:border-ink-muted ' +
  'focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--gh-primary-soft)] ' +
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-subtle ' +
  'aria-[invalid=true]:border-negative aria-[invalid=true]:focus:shadow-[0_0_0_3px_var(--gh-negative-soft)]';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg';
  /** Icon or text rendered inside, before the value. */
  leading?: ReactNode;
  trailing?: ReactNode;
  invalid?: boolean;
}

const inputSizes = {
  sm: 'h-8 text-sm px-2.5',
  md: 'h-9.5 text-[0.875rem] px-3',
  lg: 'h-11 text-base px-3.5',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', leading, trailing, invalid, className, ...rest },
  ref,
) {
  const input = (
    <input
      ref={ref}
      aria-invalid={invalid || rest['aria-invalid'] || undefined}
      className={cn(
        fieldBase,
        inputSizes[size],
        leading && 'pl-9',
        trailing && 'pr-9',
        !leading && !trailing && className,
      )}
      {...rest}
    />
  );
  if (!leading && !trailing) return input;
  return (
    <div className={cn('relative', className)}>
      {leading && (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-subtle [&_svg]:size-4">
          {leading}
        </span>
      )}
      {input}
      {trailing && (
        <span className="absolute inset-y-0 right-2.5 flex items-center text-ink-subtle [&_svg]:size-4">
          {trailing}
        </span>
      )}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, rows = 4, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || rest['aria-invalid'] || undefined}
      className={cn(
        fieldBase,
        'min-h-20 resize-y px-3 py-2.5 text-[0.875rem] leading-relaxed',
        className,
      )}
      {...rest}
    />
  );
});
